import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  List,
  Space,
  Spin,
  Tabs,
  Typography,
} from 'antd';

import { BenchmarkDrilldown } from '../ScenarioBuilder/Benchmarks';
import { JobsStrip } from '../ScenarioBuilder/Jobs';
import {
  ActivatePanel,
  ValidityEvidence,
} from '../ScenarioBuilder/ValidityPanel';
import {
  ValidityResponse,
  TimelineDocument,
} from '../ScenarioBuilder/actionTypes';
import {
  fetchScenarioValidity,
  fetchTimeline,
  recomputeScenarioValidity,
} from '../ScenarioBuilder/actionsApi';
import { readableError } from '../ScenarioBuilder/errors';
import { describeActor } from '../ScenarioBuilder/Jobs';
import './blueprintAnswerKey.css';

/**
 * Everything behind the blueprint page's Details button, in one place and
 * on no new endpoints.
 *
 * The five things an admin goes looking for after reading the coverage table
 * used to live on four different screens: the quality audit's evidence in a
 * drawer on the Scenario Builder tab, the key's own content on the Answer
 * keys list that no longer exists, the family's history on the timeline
 * drawer, the job ledger on the Jobs tab, and the activation on a panel
 * reachable from none of them. Each is a tab here, and each reuses the
 * component that already renders it, so a verdict cannot read one way in this
 * drawer and another way on the tab it came from.
 *
 * TABS COST A REQUEST, so a tab nobody opens makes none: antd mounts a pane
 * on first activation, and the two tabs that mean nothing without a key
 * (the audit and the content) refuse to ask at all when there is no key. The
 * family's timeline and its job ledger exist either way and are read either
 * way.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  blueprintId: number;
  blueprintName?: string;
  variantId?: number;
  answerKeyCode?: string | null;
  answerKeyName?: string | null;
  /** The quality audit failed, so activating over it is a thing to offer. */
  auditFailed?: boolean;
  /** An admin has taken this key out of service for new launches. */
  launchBlocked?: boolean;
  launchBlockReason?: string;
  /** Re-read the coverage document after an activation. */
  onChanged?: () => void;
};

const NoKey: React.FC<{ what: string }> = ({ what }) => (
  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={what} />
);

/** The audit's own evidence, and the one button that re-grades it. */
const AuditTab: React.FC<{ code: string; active: boolean }> = ({
  code,
  active,
}) => {
  const [response, setResponse] = useState<ValidityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setResponse(await fetchScenarioValidity(code));
    } catch (requestError) {
      setError(readableError(requestError, { what: 'This quality audit' }));
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    if (active && code) void load();
  }, [active, code, load]);

  const recompute = async () => {
    setBusy(true);
    setError('');
    try {
      setResponse(await recomputeScenarioValidity(code));
    } catch (requestError) {
      setError(readableError(requestError, { what: 'This quality audit' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      <Card
        size="small"
        title="Evidence"
        extra={
          <Button onClick={recompute} loading={busy || loading}>
            Re-run the quality audit
          </Button>
        }
      >
        {loading && !response ? (
          <Spin size="small" />
        ) : (
          <ValidityEvidence response={response} />
        )}
      </Card>
    </Space>
  );
};

/** The family's own history: every mint, derivation, publish and launch. */
const TimelineTab: React.FC<{ blueprintId: number; active: boolean }> = ({
  blueprintId,
  active,
}) => {
  const [audit, setAudit] = useState<TimelineDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchTimeline({ blueprint: blueprintId })
      .then(result => {
        if (!cancelled) setAudit(result);
      })
      .catch(requestError => {
        if (!cancelled) {
          setError(readableError(requestError, { what: 'This timeline' }));
          setAudit(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, blueprintId]);

  if (error) {
    return (
      <Alert
        className="assessment-callout"
        type="error"
        showIcon
        title={error}
      />
    );
  }
  const events = [...(audit?.events || [])].sort((a, b) =>
    String(b.at).localeCompare(String(a.at)),
  );
  return (
    <List
      loading={loading}
      size="small"
      dataSource={events}
      locale={{ emptyText: 'Nothing has happened to this scenario yet.' }}
      renderItem={event => (
        <List.Item>
          <Space direction="vertical" size={0}>
            <Typography.Text>{event.title}</Typography.Text>
            <Typography.Text type="secondary" className="bp-answer-key__muted">
              {[
                event.at ? new Date(event.at).toLocaleString() : '',
                event.kind,
                describeActor(event.actor),
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography.Text>
          </Space>
        </List.Item>
      )}
    />
  );
};

const AnswerKeyDetailsDrawer: React.FC<Props> = ({
  open,
  onClose,
  blueprintId,
  blueprintName,
  variantId,
  answerKeyCode,
  answerKeyName,
  auditFailed,
  launchBlocked,
  launchBlockReason,
  onChanged,
}) => {
  const [tab, setTab] = useState('audit');
  const [activateOpen, setActivateOpen] = useState(false);
  const code = String(answerKeyCode || '');

  const items = [
    {
      key: 'audit',
      label: 'Audit evidence',
      children: code ? (
        <AuditTab code={code} active={open && tab === 'audit'} />
      ) : (
        <NoKey what="No answer key has been derived for this version yet." />
      ),
    },
    {
      key: 'content',
      label: 'Content',
      children: code ? (
        <BenchmarkDrilldown code={code} />
      ) : (
        <NoKey what="There is no answer key to look inside yet." />
      ),
    },
    {
      key: 'timeline',
      label: 'Timeline',
      children: (
        <TimelineTab
          blueprintId={blueprintId}
          active={open && tab === 'timeline'}
        />
      ),
    },
    {
      key: 'jobs',
      label: 'Jobs',
      children:
        variantId != null ? (
          <JobsStrip target_kind="blueprint_variant" target_id={variantId} />
        ) : (
          <NoKey what="No variant is selected, so there is no job history to show." />
        ),
    },
    {
      key: 'service',
      label: 'Withdraw or activate',
      children: code ? (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {launchBlocked ? (
            <Alert
              className="assessment-callout"
              type="warning"
              showIcon
              title="This answer key has been withdrawn from new launches"
              description={
                launchBlockReason ||
                'An administrator stopped new sessions being launched against it.'
              }
            />
          ) : (
            <Typography.Text type="secondary">
              This answer key is in service: new sessions may be launched
              against it.
            </Typography.Text>
          )}
          {/* Activating over a failed audit is a signed decision, so it is
              offered only where it can be needed. A key that passed has
              nothing to sign for. */}
          {auditFailed ? (
            <Button onClick={() => setActivateOpen(true)}>
              Activate over the failed audit
            </Button>
          ) : (
            <Typography.Text>
              This answer key passed its quality audit, so there is nothing to
              activate over.
            </Typography.Text>
          )}
        </Space>
      ) : (
        <NoKey what="There is no answer key to withdraw or activate yet." />
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={960}
      destroyOnHidden
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>
            {answerKeyName || blueprintName || 'Answer key'}
          </Typography.Text>
          <Typography.Text type="secondary" className="bp-answer-key__muted">
            {[code, blueprintName].filter(Boolean).join(' · ')}
          </Typography.Text>
        </Space>
      }
    >
      <Tabs activeKey={tab} onChange={setTab} items={items} />
      {code && (
        <ActivatePanel
          open={activateOpen}
          onClose={() => setActivateOpen(false)}
          scenarioCode={code}
          scenarioName={answerKeyName || undefined}
          onActivated={() => onChanged?.()}
        />
      )}
    </Drawer>
  );
};

export default AnswerKeyDetailsDrawer;
